import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import { Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/services/api";
import { useQueryClient } from "@tanstack/react-query";
import SEO from "@/components/SEO";

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const queryClient = useQueryClient();

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) {
      return toast.error("Organization name is required");
    }
    setLoading(true);
    try {
      await api.post("/organizations", { organization_name: orgName });
      toast.success("Organization created successfully!");
      // Invalidate dashboard cache so it reflects the new organization
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      setStep(2);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to create organization");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    nav("/app");
  };

  const handleFinish = () => {
    // Invalidate dashboard cache to ensure fresh data
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    nav("/app");
  };

  return (
    <>
      <SEO
        title="Welcome to Bill Forge"
        description="Complete your setup and get started with Bill Forge."
        path="/app/onboarding"
      />
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Toaster position="top-right" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-2xl"
        >
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 ${step >= 1 ? "text-primary" : "text-muted-foreground"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {step > 1 ? <CheckCircle2 className="w-4 h-4" /> : "1"}
                </div>
                <span className="font-medium">Create Organization</span>
              </div>
              <div className={`w-16 h-0.5 ${step >= 2 ? "bg-primary" : "bg-muted"}`} />
              <div className={`flex items-center gap-2 ${step >= 2 ? "text-primary" : "text-muted-foreground"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {step > 2 ? <CheckCircle2 className="w-4 h-4" /> : "2"}
                </div>
                <span className="font-medium">Complete Setup</span>
              </div>
            </div>
          </div>

          {/* Step 1: Create Organization */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-card border border-border rounded-2xl p-8 md:p-12"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-3xl font-bold font-manrope mb-2">Create Your Organization</h1>
                <p className="text-muted-foreground">
                  Start by creating your first organization to manage your invoices and customers.
                </p>
              </div>

              <form onSubmit={handleCreateOrg} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Organization Name</label>
                  <input
                    type="text"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="e.g., Acme Corporation"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                    required
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={handleSkip}
                    className="flex-1 px-6 py-3 border border-border rounded-lg hover:bg-accent transition-colors font-medium"
                  >
                    Skip for now
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? "Creating..." : "Continue"} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Step 2: Complete */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-card border border-border rounded-2xl p-8 md:p-12 text-center"
            >
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-3xl font-bold font-manrope mb-2">You're All Set!</h1>
              <p className="text-muted-foreground mb-8">
                Your organization has been created successfully. You can now start creating invoices, managing customers, and tracking inventory.
              </p>

              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="bg-background border border-border rounded-lg p-4">
                  <div className="text-2xl mb-2">📄</div>
                  <h3 className="font-semibold mb-1">Create Invoices</h3>
                  <p className="text-sm text-muted-foreground">Generate professional invoices</p>
                </div>
                <div className="bg-background border border-border rounded-lg p-4">
                  <div className="text-2xl mb-2">👥</div>
                  <h3 className="font-semibold mb-1">Manage Customers</h3>
                  <p className="text-sm text-muted-foreground">Track your customer base</p>
                </div>
                <div className="bg-background border border-border rounded-lg p-4">
                  <div className="text-2xl mb-2">📦</div>
                  <h3 className="font-semibold mb-1">Track Inventory</h3>
                  <p className="text-sm text-muted-foreground">Monitor your stock levels</p>
                </div>
              </div>

              <button
                onClick={handleFinish}
                className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center justify-center gap-2"
              >
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </>
  );
}
